import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  CreditCard,
  LayoutDashboard,
  LogIn,
  Mail,
  MessageCircle,
  Phone,
  Send,
  UserPlus,
} from "lucide-react";
import {
  CtaSection,
  PageHero,
  Section,
  SectionHeading,
  btnOutlineDark,
  btnPrimary,
  cardClass,
  cardIconClass,
  cardTitleClass,
  useAuthActions,
} from "../components/SiteUI";

const HowItWorks = () => {
  const navigate = useNavigate();
  const { openSignup } = useAuthActions();

  const steps = [
    { id: "01", icon: UserPlus, title: "Create Company Account", text: "Sign up with your company and admin details to start your onboarding process." },
    { id: "02", icon: LogIn, title: "Sign In With Email", text: "Use the company admin email you registered. No separate username is required." },
    { id: "03", icon: CreditCard, title: "Choose Plan and Billing", text: "Select Starter, Professional, or Enterprise with Monthly or Yearly billing in GMD." },
    { id: "04", icon: Send, title: "Share Payment Evidence", text: "Send payment evidence to Gamrent admin via Email, Phone call, or WhatsApp." },
    { id: "05", icon: BadgeCheck, title: "Access is Approved", text: "SuperAdmin reviews your request and enables your dashboard access period." },
    { id: "06", icon: LayoutDashboard, title: "Run Daily Operations", text: "Manage tenants, rentals, invoices, maintenance, and reports from one dashboard." },
  ];

  const channels = [
    { name: "Email", icon: Mail },
    { name: "Phone", icon: Phone },
    { name: "WhatsApp", icon: MessageCircle },
  ];

  return (
    <div className="bg-teal-50">
      <PageHero
        eyebrow="Workflow"
        title="How Gamrent Works"
        description="A clear activation journey from signup to approved dashboard access."
      />

      <Section width="max-w-5xl">
        <div className="space-y-4">
          {steps.map((step, idx) => (
            <article key={step.id} className={`reveal reveal-delay-${Math.min(idx + 1, 6)} lift ${cardClass} flex items-start gap-4 sm:gap-5`}>
              <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-lg bg-teal-700 text-white flex items-center justify-center" aria-hidden="true">
                <step.icon className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <h2 className={cardTitleClass}>{step.title}</h2>
                <p className="text-sm text-teal-800 mt-2 leading-relaxed">{step.text}</p>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section tone="alt" width="max-w-5xl">
        <SectionHeading
          title="Payment Confirmation Channels"
          description="Use any of these channels to share payment evidence for approval."
        />
        <div className="grid sm:grid-cols-3 gap-4">
          {channels.map((channel, idx) => (
            <div key={channel.name} className={`reveal reveal-delay-${idx + 1} lift ${cardClass} text-center`}>
              <div className={`${cardIconClass} mx-auto mb-3`} aria-hidden="true">
                <channel.icon className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Channel</p>
              <p className="text-lg font-extrabold text-teal-900 mt-2">{channel.name}</p>
            </div>
          ))}
        </div>
      </Section>

      <CtaSection title="Ready to start your approval journey?">
        <button onClick={openSignup} className={btnPrimary}>
          Create Account
        </button>
        <button onClick={() => navigate("/contact")} className={btnOutlineDark}>
          Talk to Us
        </button>
      </CtaSection>
    </div>
  );
};

export default HowItWorks;
